import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// LoadingSkeleton
//
// Table skeleton, card skeleton, and page skeleton
// variants with animated pulse.
// Design: §9.14 Loading State Patterns
// ─────────────────────────────────────────────

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

/** Base skeleton pulse element */
function Skeleton({ className, style }: SkeletonProps) {
  return (
    <div
      className={cn(
        "animate-pulse rounded bg-hover",
        className
      )}
      style={{
        background: `linear-gradient(90deg, var(--bg-hover) 0%, var(--border-default) 50%, var(--bg-hover) 100%)`,
        backgroundSize: "200% 100%",
        animation: "skeleton-shimmer 1.5s infinite",
        ...style,
      }}
    />
  );
}

// ── Table Skeleton ───────────────────────────
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function TableSkeleton({ rows = 6, columns = 5, className }: TableSkeletonProps) {
  return (
    <div className={cn("w-full", className)}>
      {/* Header row */}
      <div
        className="grid gap-4 border-b border-border-subtle px-5 py-3"
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-3/4" />
        ))}
      </div>

      {/* Data rows — 44px height matching actual rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className="grid items-center gap-4 border-b border-border-subtle px-5"
          style={{
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            height: 44,
          }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton
              key={colIdx}
              className="h-3"
              style={
                colIdx === 0
                  ? { width: "70%" }
                  : colIdx === columns - 1
                    ? { width: "40%" }
                    : { width: "60%" }
              }
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Card Skeleton ────────────────────────────
interface CardSkeletonProps {
  lines?: number;
  className?: string;
}

export function CardSkeleton({ lines = 3, className }: CardSkeletonProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-card border border-border-default bg-card p-5",
        className
      )}
    >
      {/* Icon + title row */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-control" />
        <Skeleton className="h-4 w-1/3" />
      </div>
      {/* Body lines */}
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="h-3" style={{ width: i === lines - 1 ? "60%" : "100%" }} />
      ))}
    </div>
  );
}

// ── Page Skeleton ────────────────────────────
export function PageSkeleton() {
  return (
    <div className="flex flex-col gap-section">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-9 w-36 rounded-control" />
      </div>

      {/* Stat cards row */}
      <div className="grid grid-cols-4 gap-item">
        {Array.from({ length: 4 }).map((_, i) => (
          <CardSkeleton key={i} lines={1} />
        ))}
      </div>

      {/* Table */}
      <div className="rounded-card border border-border-default bg-card">
        <div className="border-b border-border-default p-5">
          <Skeleton className="h-4 w-32" />
        </div>
        <TableSkeleton rows={8} columns={5} />
        <div className="flex items-center justify-between border-t border-border-subtle p-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-48 rounded-control" />
        </div>
      </div>
    </div>
  );
}

// Add shimmer keyframe to the document via a style tag
// This is inserted once at module load time
if (typeof document !== "undefined") {
  const styleId = "kiborg-skeleton-shimmer";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes skeleton-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `;
    document.head.appendChild(style);
  }
}

export { Skeleton };
